/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * Renders a dual-agent comparison turn with wall time and token usage per side.
 */

import React from 'react';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';

import StreamingMessage from './StreamingMessage.jsx';
import { getAgentComparisonMessageStyles } from './AgentComparisonMessage.styles';

const formatModel = meta => {
  if (!meta || typeof meta !== 'object') {
    return '—';
  }

  const m = meta.model;

  if (m !== null && m !== undefined && String(m).trim() !== '') {
    return String(m).trim();
  }

  return '—';
};

const formatUsage = usage => {
  if (!usage || typeof usage !== 'object') {
    return { inTok: '—', outTok: '—' };
  }

  const inputTokens =
    typeof usage.inputTokens === 'number' && !Number.isNaN(usage.inputTokens) ? usage.inputTokens : null;
  const outputTokens =
    typeof usage.outputTokens === 'number' && !Number.isNaN(usage.outputTokens) ? usage.outputTokens : null;

  return {
    inTok: inputTokens === null ? '—' : inputTokens.toLocaleString(),
    outTok: outputTokens === null ? '—' : outputTokens.toLocaleString(),
  };
};

const resolveSideLabel = (side, intl) => {
  if (typeof side?.label === 'string' && side.label.trim() !== '') {
    return side.label.trim();
  }

  if (typeof side?.labelStringId === 'string' && side.labelStringId.trim() !== '') {
    return intl.formatMessage({ id: side.labelStringId });
  }

  if (typeof side?.profile === 'string' && side.profile.trim() !== '') {
    return side.profile;
  }

  return side?.agentRef ?? '—';
};

const AgentComparisonMessageComponent = ({ message, markdownDensity = 'compact' }) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = getAgentComparisonMessageStyles(theme);
  const sides = Array.isArray(message?.sides) ? message.sides : [];
  const isPending = Boolean(message?.isStreaming);

  return (
    <Box sx={styles.root}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={styles.titleRow}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em', lineHeight: 1.2 }}>
          {intl.formatMessage({ id: 'command_intelligence_compare_title' })}
        </Typography>
      </Stack>

      {isPending ? (
        <Box sx={styles.pending}>
          <CircularProgress size={22} thickness={4.5} />
          <CircularProgress size={22} thickness={4.5} />
        </Box>
      ) : (
        <Box sx={styles.columns}>
          {sides.map(side => {
            const { inTok, outTok } = formatUsage(side?.metadata?.usage);
            const model = formatModel(side?.metadata);

            return (
              <Box key={side?.profile ?? side?.agentRef} sx={styles.column}>
                <Box sx={styles.columnHead}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {resolveSideLabel(side, intl)}
                  </Typography>
                  {side?.error ? null : (
                    <Typography variant="caption" sx={styles.stats}>
                      {intl.formatMessage(
                        { id: 'command_intelligence_compare_stats' },
                        {
                          durationMs: typeof side?.durationMs === 'number' ? side.durationMs.toLocaleString() : '—',
                          model,
                          inTok,
                          outTok,
                          agentRef: side?.agentRef ?? '—',
                        }
                      )}
                    </Typography>
                  )}
                </Box>
                {side?.error ? (
                  <Typography variant="body2" color="error">
                    {intl.formatMessage({ id: 'command_intelligence_compare_error' }, { message: side.error })}
                  </Typography>
                ) : (
                  <StreamingMessage
                    content={side?.displayText ?? ''}
                    isStreaming={false}
                    markdownDensity={markdownDensity}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const areEqual = (prev, next) =>
  prev.message === next.message ||
  (prev.message?.crn === next.message?.crn &&
    prev.message?.isStreaming === next.message?.isStreaming &&
    prev.message?.sides === next.message?.sides &&
    prev.markdownDensity === next.markdownDensity);

const AgentComparisonMessage = React.memo(AgentComparisonMessageComponent, areEqual);

export default AgentComparisonMessage;
